import { SHButton } from "@components/design-systems";
import { useNotification } from "@hooks/useNotification";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { AnalysisStatus } from "@models/platform-analysis/enums/status";
import { CompleteDialog } from "@pages/platform-analysis/components/dialogs/complete";
import { putPlatformAnalysisStatus } from "@services/platform-analysis/platformAnalysisService";
import { useState } from "react";

export interface CompleteButtonProps {
  analysisId?: string;
  disabled?: boolean;
  onSubmit?: () => Promise<unknown>;
}

export const CompleteButton = ({
  analysisId,
  disabled,
  onSubmit,
}: CompleteButtonProps) => {
  const { notify } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const { setIsCompleted, setIsReadOnly } = usePlatformAnalysis();
  const handleOnComplete = async () => {
    if (!analysisId || !onSubmit) return;
    setIsSubmitting(true);
    const isSaved = await onSubmit();
    if (!isSaved) {
      setIsSubmitting(false);
      return;
    }
    const { isSuccess, data, message } = await putPlatformAnalysisStatus(
      analysisId,
      AnalysisStatus.Completed,
    );
    setIsSubmitting(false);
    if (isSuccess && data) {
      notify(message, {
        variant: "success",
        close: true,
      });
      setIsReadOnly(true);
      setIsCompleted(true);
      setShowDialog(false);
    } else {
      notify(message, {
        variant: "error",
        close: true,
      });
    }
  };

  return (
    <>
      <SHButton
        variant="contained"
        size="extraMedium"
        isLoading={isSubmitting}
        disabled={isSubmitting || disabled}
        onClick={() => setShowDialog(true)}
      >
        Complete
      </SHButton>
      {showDialog && (
        <CompleteDialog
          isSubmitting={isSubmitting}
          onSubmit={handleOnComplete}
          onClose={() => setShowDialog(false)}
        />
      )}
    </>
  );
};
